import { listCountStatus } from '~/modules/retailMedia/services/grocery/campaign'

import { getCampaignsService } from '..'

/**
 * Recorte da fila de revisão do anunciante (offsite).
 *
 * `has_channel` em vez de `channel=dv360,meta`: no domínio o fluxo novo é
 * identificado por *ter* canal — não existe valor sentinela para o legado.
 * Enumerar os canais de hoje faria uma campanha de um canal futuro sumir da
 * fila em silêncio, sem erro e sem estado vazio, e ninguém poderia aprová-la.
 *
 * `type` é o contexto da campanha, não o formato do criativo: filtrar por
 * `ad_type: 'banner_off_site'` perderia campanhas no dia em que existir um
 * segundo formato offsite.
 */
const queueScope = {
  type: 'off_site',
  has_channel: 'true'
} as const satisfies Pick<ResquestCampaignList, 'type' | 'has_channel'>

/** A listagem devolve muito mais do que a fila mostra; o resto é descartado */
const toQueueItem = (
  campaign: CampaignWithMetrics
): OffsiteAdvertiserReviewQueueItem => ({
  id: campaign.id,
  name: campaign.name,
  status: campaign.status,
  created_at: campaign.created_at,
  start_at: campaign.start_at,
  end_at: campaign.end_at ?? null,
  settings: campaign.settings
})

/**
 * LIST
 * Na home o `publisher_id` fica de fora: a review_queue do anunciante recorta pelo
 * token. Com `rmid` ou white label ele entra só como filtro.
 *
 * A ordenação tem default porque a do back é `campaign_status, impressions` e
 * esta tela não pede métricas: com impressões zeradas o desempate fica a cargo
 * do banco, e uma ordem instável faz a paginação repetir e pular linhas.
 */
export const listOffsiteAdvertiserReviewKey = 'listOffsiteAdvertiserReview'
export const listOffsiteAdvertiserReviewService = async ({
  publisher_id,
  page = 1,
  quantity = 50,
  status,
  order_by = 'created_at',
  order_direction = 'DESC'
}: RequestOffsiteAdvertiserReviewQueue): Promise<OffsiteAdvertiserReviewQueueResponse> => {
  const { data } = await getCampaignsService({
    ...queueScope,
    review_queue: 'advertiser_briefing',
    ...(publisher_id ? { publisher_id } : {}),
    page,
    quantity,
    status,
    order_by,
    order_direction
  })

  return {
    total: data.total,
    pages: data.pages,
    currentPage: data.currentPage,
    data: data.data.map(toQueueItem)
  }
}

/**
 * COUNT — recorte mais estreito que o da lista, de propósito: a lista mostra o
 * ciclo de vida inteiro, o badge mostra só o que exige ação. O escopo de canal,
 * esse sim, é o mesmo dos dois; se divergirem, badge e linhas se descolam no
 * dia em que um canal for adicionado.
 *
 * `pending_review`, e não `awaiting_approval`: este último existe no enum e no
 * tipo do Postgres, mas nenhum código do back o grava nem o declara na matriz
 * de transição. Toda campanha não-produto entra como `pending_review` pelo
 * `@BeforeInsert` da entidade, e é de lá que ela sai para `approved` ou
 * `rejected`. Contar o outro valor devolvia zero sempre.
 */
export const countOffsiteAdvertiserReviewKey = 'countOffsiteAdvertiserReview'
export const countOffsiteAdvertiserReviewService = async ({
  publisher_id
}: RequestOffsiteAdvertiserReviewCount = {}): Promise<{
  count: number
  available: boolean
}> => {
  const { data } = await listCountStatus({
    ...queueScope,
    review_queue: 'advertiser_briefing',
    ...(publisher_id ? { publisher_id } : {}),
    status: 'pending_review'
  })

  return {
    count: data.count,
    available: data.available === true
  }
}
